import { Injectable, inject } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { firstValueFrom } from 'rxjs';
import { PkDialogHost, type PkDialogData } from './dialog-host';

export interface PkConfirmOptions {
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  /** 삭제 등 파괴적 동작 — 확인 버튼을 위험색으로 */
  danger?: boolean;
}

export interface PkAlertOptions {
  message: string;
  title?: string;
  confirmText?: string;
}

export interface PkPromptOptions {
  message: string;
  title?: string;
  placeholder?: string;
  initialValue?: string;
  /** true면 빈 값으로 확인 불가 — 사유 필수 입력 자리에서 호출부 재검증을 없앤다 */
  required?: boolean;
  maxLength?: number;
}

/**
 * 컨펌·알림·입력 다이얼로그 서비스 — window.confirm/alert/prompt 대체 (window.* 금지).
 * 반환값은 브라우저 API와 같은 모양: confirm→boolean(취소 false), prompt→string|null(취소 null).
 * 포커스 트랩·ESC·백드롭·포커스 복원은 CDK Dialog에 위임한다.
 */
@Injectable({ providedIn: 'root' })
export class PkDialogService {
  private readonly dialog = inject(Dialog);

  confirm(options: string | PkConfirmOptions): Promise<boolean> {
    const opts = typeof options === 'string' ? { message: options } : options;
    return this.open<boolean>({ kind: 'confirm', ...opts }).then((result) => result === true);
  }

  alert(options: string | PkAlertOptions): Promise<void> {
    const opts = typeof options === 'string' ? { message: options } : options;
    return this.open<boolean>({ kind: 'alert', ...opts }).then(() => undefined);
  }

  prompt(options: string | PkPromptOptions): Promise<string | null> {
    const opts = typeof options === 'string' ? { message: options } : options;
    return this.open<string>({ kind: 'prompt', ...opts }).then((result) =>
      typeof result === 'string' ? result : null,
    );
  }

  private open<R>(data: PkDialogData): Promise<R | undefined> {
    const ref = this.dialog.open<R, PkDialogData>(PkDialogHost, {
      data,
      backdropClass: 'pk-dialog-backdrop',
      ariaLabel: data.title ?? data.message,
    });
    return firstValueFrom(ref.closed, { defaultValue: undefined });
  }
}
